/**
 * @file EmployeeMeetingDetailPage.tsx
 * @brief Employee Meeting Intel detail + speaker resolve.
 *
 * @author DES
 * @date 28-07-2026
 */

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { readBackState } from "@/shared/navigation/backState";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/feedback/ErrorState";
import { HistoryLockedState } from "@/shared/history/HistoryLockedState";
import { isHistoryLockedError } from "@/shared/history/historyLock";
import {
  GridBand,
  GridBlock,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { useCompanyMeetingDetail } from "@/features/company-meetings/api";
import { isNotesTierMeeting } from "@/features/company-meetings/schemas";
import { hasNotesPreviewFlag } from "@/features/company-meetings/notesDoc";
import { MeetingDetailView } from "@/features/company-meetings/components/MeetingDetailView";
import { EmployeeSpeakerResolvePanel } from "./EmployeeSpeakerResolvePanel";
import { MeetingFeedbackPrompt } from "./MeetingFeedbackPrompt";

export function EmployeeMeetingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useCompanyMeetingDetail(id ?? null);
  const meeting = detail.data;

  // A Meeting Notes recording lives in the Notes view, not the Meetings list
  // (ADR-030, 06-10-2026), so its back link goes there.
  // The dev Notes v2 preview (notesDoc.ts) was opened from Notes too
  // (DES 09-10-2026).
  const location = useLocation();
  const isNotes = isNotesTierMeeting(meeting) || hasNotesPreviewFlag(location.search);
  // Back to where the meeting was opened from (a to-do, Home, Inbox), else
  // the list (DES 13-10-2026).
  const back = readBackState(location.state);
  const backToList = () => navigate(back?.backTo ?? (isNotes ? "/app/notes" : "/app/meetings"));

  if (detail.isLoading) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand className="space-y-3">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-10 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </GridBand>
          <GridBand>
            <Skeleton className="h-28 w-full" />
          </GridBand>
          <GridBand>
            <Skeleton className="h-64 w-full" />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  // B39: older than this user's history window (403 HISTORY_LOCKED).
  if (isHistoryLockedError(detail.error)) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>
            <HistoryLockedState error={detail.error} onBack={backToList} />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  if (detail.error || !meeting) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>
            <ErrorState
              title="Couldn't load this meeting"
              error={
                detail.error ??
                "It may have been deleted or you no longer have access."
              }
              onRetry={() => detail.refetch()}
            />
          </GridBand>
        </GridBlock>
      </div>
    );
  }

  const isReady = meeting.status === "ready";

  return (
    <MeetingDetailView
      meeting={meeting}
      scope="employee"
      layout="phone"
      onBack={backToList}
      backLabel={back?.backLabel ?? (isNotes ? "Notes" : "Meeting Intel")}
      headerExtra={
        isReady ? (
          <MeetingFeedbackPrompt meetingId={meeting.id} meetingReady />
        ) : null
      }
      resolvePanel={
        meeting.status === "resolved_pending" ? (
          <EmployeeSpeakerResolvePanel key={meeting.id} meeting={meeting} />
        ) : undefined
      }
    />
  );
}
